import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Package, CheckCircle2, Download, Printer } from 'lucide-react';
import { BrandMark } from '../components/BrandMark';
import axios from 'axios';
import BaggageTagPrint, { triggerPrint } from '../components/BaggageTagPrint';
import { formatPickupAddress, formatDeliveryAddress } from '../lib/addressFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Receipt = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Strip any literal `{...}` wrapper that may have leaked in from older
  // Stripe success_url templates (a previous template bug double-escaped the
  // Stripe placeholder and produced `?session_id={cs_test_…}`).
  const rawSessionId = searchParams.get('session_id') || '';
  const sessionId = rawSessionId.replace(/^\{(.+)\}$/, '$1');
  const provider = (searchParams.get('provider') || 'stripe').toLowerCase();
  const razorpayBookingId = searchParams.get('booking_id') || '';
  
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [booking, setBooking] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Razorpay path — signature was already verified server-side before this
    // page loaded, so just fetch the booking directly.
    if (provider === 'razorpay' && razorpayBookingId) {
      setPolling(false);
      setPaymentStatus({ payment_status: 'paid', booking_id: razorpayBookingId });
      fetchBookingDetails(razorpayBookingId);
      return;
    }
    if (!sessionId) {
      navigate('/');
      return;
    }
    pollPaymentStatus();
  }, [sessionId, provider, razorpayBookingId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const pollPaymentStatus = async (attempts = 0) => {
    // 15 attempts × 2s = 30s total — Stripe checkout can take a few seconds
    // to flip to 'paid' after the customer clicks Pay. 30s is conservative
    // enough to cover the slowest real-world bank confirmations without
    // making the customer wait forever on an actually-failed card.
    const maxAttempts = 15;
    
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus(response.data);
        setPolling(false);
        fetchBookingDetails(response.data.booking_id);
        return;
      }
      
      if (response.data.status === 'expired') {
        setError('Payment session expired');
        setPolling(false);
        setLoading(false);
        return;
      }
      
      // Continue polling
      if (attempts < maxAttempts) {
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      } else {
        setError('Payment verification timed out. Please check your email.');
        setPolling(false);
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to verify payment status');
      setPolling(false);
      setLoading(false);
    }
  };

  const fetchBookingDetails = async (bookingId) => {
    try {
      const uid = localStorage.getItem('user_id') || '';
      const response = await axios.get(`${API}/bookings/${bookingId}`, { params: { user_id: uid } });
      setBooking(response.data);
      // Also fetch customer details for the printable tag (aadhaar etc.)
      const userId = response.data.user_id;
      if (userId) {
        try {
          const userRes = await axios.get(`${API}/users/${userId}`);
          setCustomer(userRes.data);
        } catch (e) {
          // Non-blocking — tag will fallback to booking.customer_name
          console.warn('[receipt] fetch customer failed, falling back:', e);
        }
      }
    } catch (err) {
      setError('Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const handleNewBooking = () => {
    navigate('/dashboard');
  };

  const handleGoHome = () => {
    // Receipt is always reached after a customer payment — return to customer dashboard
    navigate('/dashboard');
  };

  const handleRetryVerify = () => {
    setError('');
    setPolling(true);
    setLoading(true);
    pollPaymentStatus(0);
  };

  if (loading || polling) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="loading-screen">
        <div className="text-center">
          <div className="loading-pulse font-mono text-lg mb-2">VERIFYING PAYMENT...</div>
          <p className="text-xs text-gray-600 font-mono">PLEASE WAIT</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center card-brutalist p-8 max-w-md">
          <p data-testid="payment-error" className="font-mono text-red-600 mb-4">{error}</p>
          <p className="text-xs font-mono text-gray-600 mb-4 leading-tight">
            If you completed the payment on Stripe, give it another 10–20 seconds.
            Your booking will also be confirmed automatically once Stripe's webhook arrives.
          </p>
          <div className="flex flex-col gap-2">
            <button
              data-testid="retry-verify-btn"
              onClick={handleRetryVerify}
              className="btn-primary px-6 py-2"
            >
              CHECK AGAIN
            </button>
            <button onClick={handleGoHome} className="btn-secondary px-6 py-2">
              RETURN TO MY BOOKINGS
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!booking || !paymentStatus) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white p-4 md:p-8" data-testid="receipt-page">
      <div className="max-w-3xl mx-auto">
        {/* Success Message */}
        <div className="text-center mb-8">
          <CheckCircle2 size={64} color="#00C853" strokeWidth={2} className="mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-black uppercase mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
            BOOKING CONFIRMED
          </h1>
          <p className="text-sm text-gray-600 font-mono uppercase tracking-wide">
            PAYMENT SUCCESSFUL • REFERENCE #
            {booking.razorpay_payment_id ? (
              <span
                data-testid="receipt-payment-ref"
                className="text-black font-bold"
                title="Razorpay transaction id — share this with support if you need to track the payment"
              >
                {booking.razorpay_payment_id}
              </span>
            ) : (
              booking.baggage_tag
            )}
          </p>
        </div>

        {/* Baggage Tag Receipt */}
        <div className="baggage-tag mb-8" data-testid="baggage-tag">
          {/* Header */}
          <div className="baggage-tag-header">
            <div className="flex items-center justify-center gap-3 mb-2">
              <BrandMark size={48} textClass="text-xl md:text-2xl" />
            </div>
            <p className="text-xs font-mono uppercase tracking-wide">LUGGAGE TRANSIT SERVICE</p>
          </div>

          {/* Body */}
          <div className="baggage-tag-body">
            <div className="text-center mb-6">
              <div className="text-xs font-mono uppercase tracking-wide text-gray-600 mb-1">BAGGAGE TAG NUMBER</div>
              <div className="text-4xl font-black font-mono" data-testid="receipt-baggage-tag">{booking.baggage_tag}</div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="data-label mb-1">CUSTOMER NAME</div>
                <div className="data-value" data-testid="receipt-customer-name">{booking.customer_name}</div>
              </div>
              <div>
                <div className="data-label mb-1">BOOKING ID</div>
                <div
                  className="data-value font-mono text-[11px] break-all leading-tight"
                  title={booking.booking_id}
                  data-testid="receipt-booking-id"
                >
                  {booking.booking_id}
                </div>
                <div className="text-[9px] font-mono text-gray-500 mt-0.5 uppercase tracking-wider">
                  Share with support for queries
                </div>
              </div>
            </div>

            <div className="mb-4">
              <div className="data-label mb-1">ROUTE</div>
              <div className="data-value text-sm">
                {(booking.pickup_location || 'PICKUP').toUpperCase()} → {booking.delivery_location.toUpperCase()}
              </div>
              {formatPickupAddress(booking) && (
                <div className="mt-2 text-xs font-mono text-gray-700" data-testid="receipt-pickup-full">
                  <span className="text-gray-500">FROM: </span>{formatPickupAddress(booking)}
                </div>
              )}
              {formatDeliveryAddress(booking) && (
                <div className="text-xs font-mono text-gray-700" data-testid="receipt-delivery-full">
                  <span className="text-gray-500">TO: </span>{formatDeliveryAddress(booking)}
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <div className="data-label mb-1">PIECES</div>
                <div className="data-value" data-testid="receipt-quantity">{booking.luggage_details.quantity}</div>
              </div>
              <div>
                <div className="data-label mb-1">WEIGHT</div>
                <div className="data-value" data-testid="receipt-weight">{booking.luggage_details.weight} KG</div>
              </div>
              <div>
                <div className="data-label mb-1">TYPE</div>
                <div className="data-value text-xs">{(booking.luggage_details.luggage_type || 'BAG').toUpperCase()}</div>
              </div>
            </div>

            <div className="mb-4">
              <div className="data-label mb-1">DIMENSIONS</div>
              <div className="data-value">{booking.luggage_details.dimensions} CM</div>
            </div>

            <div className="pt-4 border-t-2 border-dashed border-gray-300">
              <div className="flex justify-between items-center">
                <div className="data-label">AMOUNT PAID</div>
                <div className="text-2xl font-black font-mono" data-testid="receipt-amount">₹{booking.amount.toFixed(2)}</div>
              </div>
            </div>
          </div>

          {/* Barcode */}
          <div className="baggage-tag-barcode">
            <div className="barcode-font">{booking.baggage_tag}</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-center">
          <button
            data-testid="print-baggage-tag-btn"
            onClick={triggerPrint}
            className="btn-primary px-6 py-3 flex items-center justify-center gap-2"
          >
            <Printer size={20} />
            PRINT BAGGAGE TAG
          </button>
          <button
            data-testid="download-receipt-btn"
            onClick={async () => {
              try {
                const userId = localStorage.getItem('user_id') || '';
                const resp = await axios.get(
                  `${API}/bookings/${booking.booking_id}/receipt/pdf`,
                  { params: { user_id: userId }, responseType: 'blob' }
                );
                const cd = resp.headers?.['content-disposition'] || '';
                const match = cd.match(/filename="?([^";]+)"?/i);
                const fname = match ? match[1] : `LFJ-Receipt-${booking.baggage_tag || booking.booking_id}.pdf`;
                const blob = new Blob([resp.data], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fname;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              } catch (err) {
                let msg = 'Could not download receipt';
                if (err?.response?.data instanceof Blob) {
                  try { msg = JSON.parse(await err.response.data.text())?.detail || msg; } catch { /* ignore */ }
                } else {
                  msg = err?.response?.data?.detail || msg;
                }
                alert(msg);
              }
            }}
            className="btn-secondary px-6 py-3 flex items-center justify-center gap-2"
          >
            <Download size={20} />
            DOWNLOAD RECEIPT
          </button>
          <button
            data-testid="track-from-receipt-btn"
            onClick={() => navigate(`/track?tag=${booking.baggage_tag}`)}
            className="btn-secondary px-6 py-3 flex items-center justify-center gap-2"
          >
            TRACK SHIPMENT
          </button>
          <button
            data-testid="new-booking-btn"
            onClick={handleNewBooking}
            className="btn-primary px-6 py-3"
          >
            GO TO DASHBOARD
          </button>
        </div>

        {/* Printable baggage tag (hidden on screen, only visible when printing) */}
        <BaggageTagPrint booking={booking} customer={customer} />

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-600 font-mono">
            Please keep this receipt for your records. Our team will contact you for pickup scheduling.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Receipt;